import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { PageCard } from '@/components/ui/page-card'
import { Text } from '@/components/ui/text'
import type { UpgradeFailureReason } from '@/lib/auth-flow'
import { generateTitle } from '@/lib/utils'

// Claim the guest progress is unchanged only when the server verified it (a conflict).
const copy: Record<UpgradeFailureReason, { title: string; description?: string; body: string }> = {
  conflict: {
    title: 'Couldn’t Link Your Progress',
    description: 'Your guest progress is unchanged.',
    body: 'This account already has progress in this game. Return to your game and try again with a different account.',
  },
  alreadySaved: {
    title: 'Progress Already Saved',
    body: 'Return to your game.',
  },
  // This link can't finish, but another attempt may have: e.g. it expired, was replaced or
  // canceled, or was opened without the guest's session.
  invalid: {
    title: 'Couldn’t Link Your Progress',
    description: 'This link can no longer be used.',
    body: 'Return to your game to check your progress or start a new attempt.',
  },
}

export const Route = createFileRoute('/upgrade-failed')({
  validateSearch: z.object({ reason: z.enum(['conflict', 'alreadySaved', 'invalid']).optional() }),
  component: UpgradeFailed,
  head: ({ match }) => ({
    meta: [{ title: generateTitle(copy[match.search.reason ?? 'invalid'].title) }],
  }),
})

function UpgradeFailed() {
  const { reason } = Route.useSearch()
  const { title, description, body } = copy[reason ?? 'invalid']
  return (
    <PageCard title={title} description={description}>
      <Text block size={14}>
        {body} You can close this tab.
      </Text>
    </PageCard>
  )
}
